import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProviderCard from "./ProviderCard";
import {
  createProviderIssueReport,
  getProviders,
  getUserFavouriteProviderIds,
  toggleProviderFavourite,
} from "./utils/providerNetworkApi";
import "../../../css/ProviderNetwork/ProviderNetwork.css";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import { Helmet } from "react-helmet-async";
import PrimaryButton from "../AML/Components/Buttons/PrimaryButton";
import SearchField from "../../DesignSystem/SearchField";
import SecondaryButton from "../AML/Components/Inputs/SearchInput";
import { useUserEmail } from "../../../context/UserEmailContext";
import { ReactComponent as BackIcon } from "../../../assets/icons/Back.svg";
import { ReactComponent as BookmarkIcon } from "../../../assets/icons/providerNetwork/Bookmark.svg";
import { ReactComponent as BookmarkFilledIcon } from "../../../assets/icons/providerNetwork/Bookmark (filled).svg";
import { ReactComponent as HelpIcon } from "../../../assets/icons/providerNetwork/HelpIcon.svg";
import ReportProviderIssueModal from "./components/ReportProviderIssueModal";

const PROVIDER_SCROLL_KEY = "providerNetworkScrollTop";
const PROVIDER_SEARCH_KEY = "providerNetworkSearch";
const PROVIDER_SHOULD_RESTORE_SCROLL_KEY = "providerNetworkShouldRestoreScroll";

const ProviderNetworkPage = () => {
  const navigate = useNavigate();
  const { userEmail } = useUserEmail();
  const mainContainerRef = useRef(null);
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueSubmitting, setIssueSubmitting] = useState(false);
  const [providers, setProviders] = useState([]);
  const [search, setSearch] = useState(() => {
    return sessionStorage.getItem(PROVIDER_SEARCH_KEY) || "";
  });
  const [loading, setLoading] = useState(true);

  const breadcrumbItems = [
    { label: "LOA Toolkit", to: "/portal/loa-toolkit" },
    { label: "Provider Index" },
  ];

  const handleSearchChange = (e) => {
    const value = e.target.value;

    setSearch(value);
    sessionStorage.setItem(PROVIDER_SEARCH_KEY, value);
    sessionStorage.removeItem(PROVIDER_SHOULD_RESTORE_SCROLL_KEY);

    if (mainContainerRef.current) {
      mainContainerRef.current.scrollTop = 0;
    }
  };

  const fetchProviders = async () => {
    setLoading(true);

    try {
      const [providerData, favouriteIds] = await Promise.all([
        getProviders(),
        getUserFavouriteProviderIds(userEmail),
      ]);

      const favouriteSet = new Set(favouriteIds);

      // Log only the totals
      // console.log("Total providers:", providerData.length);
      const totalRecords = providerData.reduce(
        (sum, p) => sum + (Number(p.planCount) || 0),
        0,
      );
      // console.log("Total records:", totalRecords);

      setProviders(
        providerData.map((provider) => ({
          ...provider,
          isFavourite: favouriteSet.has(provider.id),
        })),
      );
    } catch (err) {
      // console.error(err);
      // alert(err.message || "Failed to load providers.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchProviders();
  }, []);

  const filteredProviders = useMemo(() => {
    const term = search.trim().toLowerCase();

    return providers.filter((provider) => {
      const matchesSearch =
        !term ||
        provider.providerName?.toLowerCase().includes(term) ||
        provider.providerNameLower?.includes(term) ||
        (provider.plans &&
          provider.plans.some(
            (plan) =>
              plan.planName?.toLowerCase().includes(term) ||
              plan.planNameLower?.includes(term),
          ));

      const matchesBookmark = !showBookmarkedOnly || provider.isFavourite;

      return matchesSearch && matchesBookmark;
    });
  }, [providers, search, showBookmarkedOnly]);

  useEffect(() => {
    if (loading) return;

    const shouldRestore = sessionStorage.getItem(
      PROVIDER_SHOULD_RESTORE_SCROLL_KEY,
    );

    if (shouldRestore !== "true") return;

    const savedScrollTop = sessionStorage.getItem(PROVIDER_SCROLL_KEY);

    if (!savedScrollTop) return;

    const restoreScroll = () => {
      const container = mainContainerRef.current;

      if (!container) return;

      container.scrollTop = Number(savedScrollTop);
    };

    requestAnimationFrame(() => {
      restoreScroll();

      setTimeout(restoreScroll, 100);
      setTimeout(() => {
        restoreScroll();

        sessionStorage.removeItem(PROVIDER_SHOULD_RESTORE_SCROLL_KEY);
      }, 300);
    });
  }, [loading]);

  const handleToggleFavourite = async (provider) => {
    try {
      await toggleProviderFavourite(
        provider.id,
        provider.isFavourite,
        userEmail,
      );

      setProviders((prev) =>
        prev.map((item) =>
          item.id === provider.id
            ? {
                ...item,
                isFavourite: !item.isFavourite,
              }
            : item,
        ),
      );
    } catch (err) {
      // console.error(err);
      // alert(err.message || "Failed to update bookmark.");
    }
  };

  const handleOpenProvider = (providerId) => {
    const scrollTop = mainContainerRef.current?.scrollTop || 0;

    sessionStorage.setItem(PROVIDER_SCROLL_KEY, String(scrollTop));
    sessionStorage.setItem(PROVIDER_SEARCH_KEY, search);
    sessionStorage.setItem(PROVIDER_SHOULD_RESTORE_SCROLL_KEY, "true");

    navigate(`/provider-index/${providerId}`);
  };

  // Calculate totals for display
  const totalProviders = providers.length;
  const totalRecords = providers.reduce(
    (sum, p) => sum + (Number(p.planCount) || 0),
    0,
  );

  const handleOpenIssueModal = () => {
    setIssueModalOpen(true);
  };

  const handleCloseIssueModal = () => {
    if (issueSubmitting) return;
    setIssueModalOpen(false);
  };

  const handleSubmitIssueReport = async ({
    providerName,
    issueType,
    details,
  }) => {
    try {
      setIssueSubmitting(true);
      setIssueModalOpen(false);

      await createProviderIssueReport({
        providerId: "",
        providerName,
        issueType,
        details,
        reportedBy: userEmail || localStorage.getItem("userEmail") || "",
        submittedAt: new Date().toISOString(),
      });

      setIssueModalOpen(false);
    } catch (error) {
      console.error("Error submitting provider issue report:", error);
      // alert(
      //   error.message || "Unable to submit issue report. Please try again.",
      // );
    } finally {
      setIssueSubmitting(false);
    }
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Provider Index</title>
      </Helmet>

      <Sidebar />
      <div
        className="Main-Container Lavender provider-network"
        ref={mainContainerRef}
      >
        <div className="provider-network-page">
          <Breadcrumb items={breadcrumbItems} />

          <div className="provider-network-header">
            <div className="provider-network-header-info">
              <div
                className="FP_H3 provider-network-title"
                //
              >
                <BackIcon onClick={() => window.history.back()} /> Provider
                Index
              </div>
              <span className="FP_Help_Text"></span>
              <div className="FP_Content grey">
                {totalProviders} providers • {totalRecords} records
                {/* Browse through the directory for financial service providers. */}
              </div>
            </div>
            <div className="provider-network-header-info">
              <div className="provider-network-actions">
                <button
                  type="button"
                  className={`provider-network-bookmark-filter ${
                    showBookmarkedOnly ? "is-active" : ""
                  }`}
                  onClick={() => {
                    sessionStorage.removeItem(
                      PROVIDER_SHOULD_RESTORE_SCROLL_KEY,
                    );
                    setShowBookmarkedOnly((prev) => !prev);

                    if (mainContainerRef.current) {
                      mainContainerRef.current.scrollTop = 0;
                    }
                  }}
                >
                  {showBookmarkedOnly ? (
                    <BookmarkFilledIcon />
                  ) : (
                    <BookmarkIcon />
                  )}
                </button>
                <SearchField
                  value={search}
                  placeholder="Provider name/Plan name"
                  onChange={handleSearchChange}
                />

                {/* <SecondaryButton
                type="button"
                label="Add Provider"
                onClick={() => navigate("/ProviderNetwork/Add")}
              >
                Add Provider
              </SecondaryButton> */}
              </div>
              <div className="provider-details-report-issue FP_Content">
                <HelpIcon /> Missing or incorrect info?{" "}
                <button
                  type="button"
                  className="provider-details-report-issue-btn FP_Content_Semibold"
                  onClick={handleOpenIssueModal}
                >
                  Report an issue
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="provider-network-empty">Loading providers...</div>
          ) : filteredProviders.length ? (
            <div className="provider-network-grid">
              {filteredProviders.map((provider) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  onClick={() => handleOpenProvider(provider.id)}
                  onToggleFavourite={() => handleToggleFavourite(provider)}
                />
              ))}
            </div>
          ) : (
            <div className="provider-network-empty">
              {showBookmarkedOnly
                ? "No bookmarked providers found."
                : "No providers found."}
            </div>
          )}
        </div>
      </div>
      <ReportProviderIssueModal
        isOpen={issueModalOpen}
        provider={null}
        onClose={handleCloseIssueModal}
        onSubmit={handleSubmitIssueReport}
        loading={issueSubmitting}
      />
    </div>
  );
};

export default ProviderNetworkPage;
